/* terminal/types */
import * as t from "io-ts";
import { BPGiftCardsConfiguration } from "./types/BPGiftCardConfiguration";
import { PayAtTable } from "./types/PayAtTable";

function tOptional<T extends t.Mixed>(typeThing: T) {
  return t.union([t.undefined, t.null, typeThing]);
}
const tId = t.union([t.undefined, t.null, t.string]);

const ErrorCodec = t.type(
  {
    code: t.string,
    detail: t.string,
    title: t.string,
  },
  "ErrorCodec"
);

const AdditionalDataCodec = t.type({
  Tag: t.string,
  Value: t.string,
  Encrypted: t.boolean,
});

const ProcTerminalCodec = t.intersection([
  t.type({
    // ProcessorName: t.unknown,
  }),
  t.partial({
    ProcessorName: t.string,
    TID: t.string,
    MID: t.string,
    STAN: t.string,
    PPID: t.string,
    AdditionalData: t.array(AdditionalDataCodec),
    ReversalPending: t.boolean,
    CompletionPending: t.boolean,
  }),
]);

const TerminalAppCodec = t.intersection([
  t.type({
    VersionString: t.string,
    Variant: t.string,
    AppName: t.string,
  }),
  t.partial({
    UpdateGroup: t.string,
    isEditAble: t.boolean,
  }),
]);

export type Processor = t.TypeOf<typeof ProcTerminalCodec>;

const TotalsCodec = t.type({
  NetSettlement: t.number,
  PurAmount: t.number,
  PurCount: t.number,
  PurRevAmount: t.number,
  PurRevCount: t.number,
  RefAmount: t.number,
  RefCount: t.number,
  RefRevAmount: t.number,
  RefRevCount: t.number,
  CashAmount: t.number,
  CashCount: t.number,
  CashRevAmount: t.number,
  CashRevCount: t.number,
  AuthAmount: t.number,
  AuthCount: t.number,
  SurAmount: t.number,
  SurCount: t.number,
  SurRevAmount: t.number,
  SurRevCount: t.number,
});

const SurchargePercentageCodec = t.type({
  CardType: t.string,
  Percentage: t.number,
  SurchargeGST: t.number,
});
export type SurchargePercentage = t.TypeOf<typeof SurchargePercentageCodec>;

const SurchargeV2Codec = t.type({
  CardType: t.string,
  SurchargeType: t.string,
  PurchaseSurchargeValue: t.number,
  CashoutSurchargeValue: t.number,
  GSTValue: t.number,
});
export type SurchargeV2 = t.TypeOf<typeof SurchargeV2Codec>;

const TippingV2Codec = t.type({
  TippingMode: t.string,
  TipWithPercentage1: t.number,
  TipWithPercentage2: t.number,
  TipWithPercentage3: t.number,
});
export type TippingV2 = t.TypeOf<typeof TippingV2Codec>;

const OfflineConfigCodec = t.type({
  Enabled: t.string,
  OfflineFirst: t.string,
  OpenLoopFloorLimit: t.number,
  CloseLoopFloorLimit: t.number,
  LimitTotal: t.number,
  LimitCount: t.number,
});
export type OfflineConfig = t.TypeOf<typeof OfflineConfigCodec>;

const OfflineLimitsCodec = t.type({
  Category: t.string,
  Product: t.string,
  Amount: t.number,
});
export type OfflineLimits = t.TypeOf<typeof OfflineLimitsCodec>;

const AttributeCodec = t.intersection([
  t.type({
    TerminalType: t.string,
    SerNo: t.string,
  }),
  t.partial({
    DateInjected: t.string,
    CryptoID: t.string,
    sSKman_SHA256_PKtcu: t.string,
    Shard: t.number,
    Manufacturer: t.string,
    Model: t.string,
    ModelExtended: t.string,
    OS: t.string,
    MCC: t.string,
    MID: t.string,
    TID: t.string,
    CardPrompts: t.string,
    LowValueTransactionLimit: t.string,
    CPATName: t.string,
    POIID: t.string,
    PPID: t.string,
    Processor: t.array(ProcTerminalCodec),
    Location: t.string,
    Address1: t.string,
    Address2: t.string,
    TZ: t.string,
    Locale: t.string,
    AddressState: t.string,
    Note: t.string,
    Host: t.string,
    Port: t.number,
    HostBackup: t.string,
    PortBackup: t.number,
    URL: t.string,
    URLBackup: t.string,
    URLUpgrade: t.string,
    URLUpgradeBackup: t.string,
    AXISPayVersion: t.string,
    Receipt: t.string,
    CustomerReceiptPrint: t.string,
    ReceiptMerchant: t.string,
    MerchantReceiptPrint: t.string,
    SKtcu: t.string, // Only available for terminal simulator
    PKtcu: t.string,
    PKtcuMAC: t.string,
    RNtcu: t.string,
    KCA: t.string,
    KCAKCV: t.string,
    KMACH: t.string,
    KMACHKCV: t.string,
    TMK1: t.string,
    TMK1KCV: t.string,
    TMK2: t.string,
    TMK2KCV: t.string,
    PPASN: t.string,
    KMACI: t.string,
    TPK: t.string,
    TPKKCV: t.string,
    TAKs: t.string,
    TAKsKCV: t.string,
    TAKr: t.string,
    TAKrKCV: t.string,
    TEKs: t.string,
    TEKsKCV: t.string,
    TEKr: t.string,
    TEKrKCV: t.string,
    STAN: t.string,
    LastLogin: t.string,
    LastTxnID: t.string,
    TxnCountSinceLastLogin: t.number,
    RunningTotals: TotalsCodec,
    State: t.string,
    Standalone: t.string,
    CloudPOS: t.string,
    POSApp: t.string,
    ShowNavigation: t.string,
    PaymentMethods: t.array(t.string),
    FeatureCashOut: t.string,
    FeaturePurWithCash: t.string,
    FeaturePurWithTip: t.string,
    FeatureRefund: t.string,
    FeaturePreAuth: t.string,
    FeatureCompletion: t.string,
    FeatureManualSettlement: t.string,
    MediumPriority: t.string,
    TempPassword: t.string,
    EMVConfigName: t.string,
    TokenWalletFileConfigName: t.string,
    FeatureCashOutLimit: t.number,
    FeatureRefundLimit: t.number,
    FeatureRefundDailyLimit: t.number,
    FeatureMOTO: t.string,
    FeatureUpdaterEnable: t.string,
    FeatureFusionAPILoggingEnabled: t.string,
    AllowBasketData: t.string,
    ReceiptPrint: t.string,
    SurchargeVersion: t.string,
    SurchargeType: t.string,
    Surcharge: t.boolean,
    SurchargeTip: t.boolean,
    SurchargeGST: t.boolean,
    EnableSurchargeV2: t.string,
    EnableTippingV2: t.string,
    EnableSurchargeSettingsOnTerminal: t.string,
    SurchargeMinSaleAmount: t.number,
    SurchargeMaxSaleAmount: t.number,
    SurchargeV2Toggle: t.string,
    SurchargePercentage: t.array(SurchargePercentageCodec),
    SurchargeV2: t.array(SurchargeV2Codec),
    CloseLoopConfigName: t.string,
    TippingV2: TippingV2Codec,
    OfflineConfig: OfflineConfigCodec,
    OfflineLimits: t.array(OfflineLimitsCodec),
    TipWithPercentagePercentage1: t.number,
    TipWithPercentagePercentage2: t.number,
    TippingMode: t.string,
    SurchargeShowDisclaimer: t.boolean,
    SurchargeDisclaimerText: t.string,
    FixedSurchargePercentage: t.number,
    FixedSurchargeAmount: t.number,
    TipWithPercentagePercentage3: t.number,
    SignatureTimeOut: t.string,
    ResultTimeout: t.string,
    FallbackRetryCount: t.string,
    StartupMode: t.string,
    IdleBitmap: t.union([t.undefined, t.null, t.string]),
    MCRMinAmount: t.number,
    MastercardMCRThreshold: t.number,
    VisaMCRThreshold: t.number,
    WelfareRestriction: t.boolean,
    AutoSettleEnabled: t.boolean,
    FeatureLastSettle: t.string,
    FeaturePreSettle: t.string,
    InactivityTimeout: t.number,
    ScreenDimmingTimeoutMs: t.number,
    CloudPosTimeout: t.number,
    DuplicateTxnCheck: t.string,
    BpCardSupport: t.string,
    BPGiftCardsConfig: BPGiftCardsConfiguration,
    IdleOnAmountEntry: t.boolean,
    TerminalStatusReportingEnabled: t.boolean,
    TerminalStatusReportingInterval: t.number,
    NetworkConnectionManagerEnabled: t.boolean,
    TerminalGPSEnabled: t.boolean,
    TerminalGPSUpdateIntervalSeconds: t.number,
    FeatureBPGiftCards: t.string,
    FeatureMentorLite: t.string,
    MentorLiteUrl: t.string,
    NoCashOutInDebit: t.boolean,
    FeatureLiteThemeEnabled: t.string,
    FeatureCryptoDotComEnabled: t.string,
    FeatureCryptoDotComRefundsEnabled: t.string,
    WindowForCDCRefundOptionSeconds: t.number,
    FeatureQantasLoyaltyEnabled: t.string,
    FeatureQantasLoyaltyCashPasswordProtect: t.string,
    KeyEntryPromptPassword: t.string,
    MOTOPromptPassword: t.string,
    FeatureQantasLoyaltyCashPassword: t.string,
    QantasLoyaltyLogoUrl: t.string,
    FeaturePartialPaymentEnabled: t.string,
    FeatureFixedAmountEntryEnabled: t.string,
    FixedAmountEntryUseUserSupplied: t.string,
    FixedAmountEntryTitle: t.string,
    FixedAmountEntryValue1: t.number,
    FixedAmountEntryValue2: t.number,
    FixedAmountEntryValue3: t.number,
    FixedAmountEntryValue4: t.number,
    PinRetryAttemptCount: t.number,
    FeatureShellContactless: t.string,
    FeaturePurchaseWithPreAuthAndCompletion: t.string,
    Applications: t.array(TerminalAppCodec),
    PayAtTableEnabled: t.string,
    PayAtTable: tOptional(PayAtTable),
    FeatureA2BTaxiModeEnabled: t.string,
    FeatureCabchargeFastCardEnabled: t.string,
    FeatureDRCLoyaltyEnabled: t.string,
    DRCMinLoyaltyAmount: t.number,
    DRCStoreId: t.string,
    DRCLoyaltyLogoUrl: t.string,
    FeatureDRCLoyaltyCashPasswordProtect: t.string,
    FeatureDRCLoyaltyCashPassword: t.string,
    groups: tOptional(t.array(t.number)),
    BatteryLimit: t.number,
    URLUpgradeFirmware: t.string,
    URLUpgradeFirmwareBackup: t.string,
    DockerFirmware: t.string,
    DockerFirmwareBackup: t.string,
    InstalledDockerFirmware: t.string,
    AutoSoftwareUpdateEnabled: t.string,
    AutoUpdateTimeFrom: t.string,
    AutoUpdateTimeTo: t.string,
    EnableSelfInstall: t.string,
    EnableStationeryOrder: t.string,
    EnableHTMLReceipts: t.string,
    PrintTransactionListing: t.boolean,
    MerchantReceiptDisplayName: t.string,
    CustomerReceiptDisplayName: t.string,
    PromptCustomerReceiptFirst: t.boolean,
    DisableLogin: t.string,
    TransitMode: t.string,
    HTMLReceipts: t.array(t.string),
    FeatureCashOutLimitEnabled: t.string,
    FeaturePairWithPOSEnabled: t.string,
    FeatureFleetCardAuthCompletion: t.string,
    TransitBlockListRefreshIntervalMs: t.number,
    ShowBackButtonOnPresentCardScreen: t.string,
    ShowCancelButtonOnPresentCardScreen: t.string,
    ReversalManagedOnHost: t.string,
    PayOnOtherTerminal: t.string,
    EnableSplitPayments: t.string,
    ShowStatusBar: t.string,
    USBPOS: t.string,
    PreAuth: tOptional(
      t.partial({
        ExpiryDays: tOptional(t.number),
        VariancePercentage: tOptional(t.number),
      })
    ),

    // CS TERMINAL
    Activated: t.string,
    RefreshTerminalConfig: t.string,
    EBID: t.string,
    WorkstationId: t.string,
    ProcessingTimeout: t.number,
    HealthCheckInterval: t.number,
    AdminFnCode: t.string,
    PreSharedKey: t.string,
    HashedHotCard: t.string,
    HashedHotCardEnabled: t.string,
    IsHotcardDeltaDownloadAllowed: t.boolean
  }),
]);

const SectionsCodec = t.partial({
  merchant: t.boolean,
  processor: t.boolean,
  hosting: t.boolean,
  hostingUpgrade: t.boolean,
  location: t.boolean,
  receipts: t.boolean,
  configuration: t.boolean,
});

export type SectionKeysType =
  | "merchant"
  | "processor"
  | "hosting"
  | "hostingUpgrade"
  | "location"
  | "receipts"
  | "configuration";

const DataCodec = t.intersection([
  t.type({
    id: tId,
    type: t.string,
    attributes: AttributeCodec,
  }),
  t.partial({
    createdAt: t.string,
    updatedAt: t.string,
    selected: t.boolean, // used for table
    bulkTerminalIds: t.array(t.string),
    sections: SectionsCodec,
  }),
]);
export type Data = t.TypeOf<typeof DataCodec>;

const MetaCodec = t.type({
  totalObjects: tOptional(t.number),
});
export type Meta = t.TypeOf<typeof MetaCodec>;

export const TerminalCodec = t.type(
  {
    errors: tOptional(t.union([t.undefined, t.array(ErrorCodec)])),
    data: tOptional(t.array(DataCodec)),
    meta: tOptional(MetaCodec),
  },
  "TerminalCodec"
);

export type Terminal = t.TypeOf<typeof TerminalCodec>;

export enum TerminalActionTypes {
  FETCH = "@@terminal/FETCH",
  FETCH_SUCCESS = "@@terminal/FETCH_SUCCESS",
  FETCH_ERROR = "@@terminal/FETCH_ERROR",
  SAVE_REQUEST = "@@terminal/SAVE_REQUEST",
  SAVE_BULK = "@@terminal/SAVE_BULK",
  SAVE_SUCCESS = "@@terminal/SAVE_SUCCESS",
  SAVE_ERROR = "@@terminal/SAVE_ERROR",
  DELETE_REQUEST = "@@terminal/DELETE_REQUEST",
  DELETE_SUCCESS = "@@terminal/DELETE_SUCCESS",
  DELETE_ERROR = "@@terminal/DELETE_ERROR",
  UPLOAD_FILE = "@@terminal/UPLOAD_FILE",
  UPLOAD_FILE_SUCCESS = "@@terminal/UPLOAD_FILE_SUCCESS",
  UPLOAD_FILE_ERROR = "@@terminal/UPLOAD_FILE_ERROR",
  UPDATE_STATE = "@@terminal/UPDATE_STATE",
  DOWNLOAD_REQUEST = "@@terminal/DOWNLOAD_REQUEST",
}

export interface TerminalApp {
  VersionString: string;
  Variant: string;
  AppName: string;
  UpdateGroup?: string;
  isEditAble?: boolean;
}

export interface TerminalState {
  readonly loading: boolean;
  readonly data: Terminal | undefined;
  readonly errors?: string;
}
